import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { DefaultFormComponent, FormServiceInterface } from '@hutsix/ngxh6';
import { CSPUserService } from '../../../../_shared/services/user/cdrc-csp-user.service';
import * as L from 'leaflet';

/**
 * Custom form for CheckInLocation. Most admins won't know lat/long, so instead of
 * raw number inputs this presents a map: search a place, use the device location,
 * or click/drag a pin — the coordinates are written into the form model.
 */
@Component({
    selector: 'app-check-in-location-form',
    templateUrl: './check-in-location-form.component.html',
    styleUrls: ['./check-in-location-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CheckInLocationFormComponent extends DefaultFormComponent {
    public searchQuery = '';
    public searchError = '';

    private map?: L.Map;
    private marker?: L.Marker;
    private mapInitialised = false;

    constructor(
        @Inject('FormService') public formService: FormServiceInterface,
        @Inject('UserService') public userService: CSPUserService,
        public location: Location,
        public cdRef: ChangeDetectorRef,
    ) {
        super(formService, userService, location, cdRef);
    }

    // Fires once the map container is rendered (after the form view loads).
    @ViewChild('mapEl') set mapEl(el: ElementRef | undefined) {
        if (el && !this.mapInitialised) {
            this.mapInitialised = true;
            setTimeout(() => this.initMap(el.nativeElement), 0);
        }
    }

    private initMap(el: HTMLElement): void {
        const lat = this.toNum(this.form.model?.latitude);
        const lng = this.toNum(this.form.model?.longitude);
        const hasInitial = lat !== null && lng !== null;

        this.map = L.map(el).setView(hasInitial ? [lat!, lng!] : [-23.698, 133.881], hasInitial ? 15 : 5);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(this.map);

        if (hasInitial) {
            this.setMarker(lat!, lng!, false);
        }

        this.map.on('click', (e: L.LeafletMouseEvent) => this.setMarker(e.latlng.lat, e.latlng.lng, false));
        setTimeout(() => this.map?.invalidateSize(), 150);
    }

    private setMarker(lat: number, lng: number, recenter = true): void {
        const icon = L.divIcon({
            html: '<div class="check-in-pin"><i class="fa fa-map-marker-alt"></i></div>',
            className: 'check-in-marker',
            iconSize: [28, 28],
            iconAnchor: [14, 14],
        });

        if (!this.marker) {
            this.marker = L.marker([lat, lng], { draggable: true, icon }).addTo(this.map!);
            this.marker.on('dragend', () => {
                const p = this.marker!.getLatLng();
                this.writeCoords(p.lat, p.lng);
            });
        } else {
            this.marker.setLatLng([lat, lng]);
        }

        if (recenter) {
            this.map?.panTo([lat, lng]);
        }
        this.writeCoords(lat, lng);
    }

    private writeCoords(lat: number, lng: number): void {
        this.form.model.latitude = Math.round(lat * 1e6) / 1e6;
        this.form.model.longitude = Math.round(lng * 1e6) / 1e6;
        this.onModelChanged(this.form.model);
        this.cdRef.detectChanges();
    }

    public hasCoords(): boolean {
        return this.toNum(this.form.model?.latitude) !== null && this.toNum(this.form.model?.longitude) !== null;
    }

    public async search(): Promise<void> {
        this.searchError = '';
        const q = this.searchQuery?.trim();
        if (!q) {
            return;
        }
        if (q.length > 150) {
            this.searchError = 'Search text is too long.';
            this.cdRef.detectChanges();
            return;
        }

        // Abort if the geocoder hangs so the search can't block indefinitely.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`, {
                headers: { Accept: 'application/json' },
                signal: controller.signal,
            });
            const data = await res.json();
            // Only the coordinates are used, and only after validating they're real numbers.
            const lat = parseFloat(data?.[0]?.lat);
            const lng = parseFloat(data?.[0]?.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                this.searchError = 'No match found — try a nearby town, or click the map directly.';
                this.cdRef.detectChanges();
                return;
            }
            this.map?.setView([lat, lng], 15);
            this.setMarker(lat, lng, false);
        } catch {
            this.searchError = 'Search is unavailable right now — click the map to set the location.';
            this.cdRef.detectChanges();
        } finally {
            clearTimeout(timeout);
        }
    }

    public useMyLocation(): void {
        this.searchError = '';
        if (!navigator.geolocation) {
            this.searchError = 'Location is not available on this device.';
            return;
        }

        navigator.geolocation.getCurrentPosition(
            pos => {
                const { latitude, longitude } = pos.coords;
                this.map?.setView([latitude, longitude], 15);
                this.setMarker(latitude, longitude, false);
                this.cdRef.detectChanges();
            },
            () => {
                this.searchError = 'Could not get your location. Allow location access, or click the map.';
                this.cdRef.detectChanges();
            },
            { enableHighAccuracy: true },
        );
    }

    private toNum(v: any): number | null {
        if (v === null || v === undefined || v === '') {
            return null;
        }
        const n = Number(v);
        return isNaN(n) ? null : n;
    }

    override ngOnDestroy(): void {
        this.map?.remove();
        super.ngOnDestroy();
    }
}
